import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import {
  device,
  useBreakpoints,
  LAYOUT_CONSTANTS,
} from "../../lib/breakpoints";
import {
  getLeaseEndDate,
  isTenantActive,
  daysUntilLeaseEnd,
} from "../../lib/leaseUtils";
import {
  Col,
  Row,
  Card,
  Button,
  Input,
  InputNumber,
  Select,
  Form,
  message,
  Tooltip,
  Modal,
  List,
  Alert,
  Skeleton,
  Spin,
  Collapse,
} from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DollarOutlined,
  HomeOutlined,
  FileTextOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  WarningOutlined,
  ScheduleOutlined,
  ApartmentOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  MinusOutlined,
  PlusOutlined,
  RiseOutlined,
  AccountBookOutlined,
  FilePdfOutlined,
  FileTextOutlined as FileTextIcon,
  CheckCircleOutlined as CheckIcon,
  AlertOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  LockOutlined,
} from "@ant-design/icons";
import LeaseModal from "../../components/LeaseModal";
import RentManagementModal from "../../components/RentManagementModal";
import DelinquencyReportModal from "../../components/DelinquencyReportModal";
import RentRollReportModal from "../../components/RentRollReportModal";
import OwnerStatementsModal from "../../components/OwnerStatementsModal";
import TaxSummariesModal from "../../components/TaxSummariesModal";
import GenerateLenderPacketButton from "../../components/GenerateLenderPacketButton";
import { Spacer } from "../../styles/CommonElements";
import { AuthContext } from "../../context/AuthProvider";
import { shouldShowScaleUpgradeForFullOs } from "../../helpers/accountProduct";
import ScaleUpgradeModal from "../../components/ScaleUpgradeModal";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";
import { trackEvent } from "../../helpers/mixpanel";
import {
  trackPageView,
  trackDashboardInteraction,
  trackCardClick,
  trackTabChange,
  trackButtonClick,
  trackModalInteraction,
  trackFormSubmission,
  trackDataAction,
  trackEmptyStateAction,
  trackTimeOnPage,
} from "../../helpers/dashboardAnalytics";
import { APP_NAME } from "../../helpers/constants";
import SummaryCard from "../../components/SummaryCard";
import EmptyState from "../../components/EmptyState";

import { useNavigate, useLocation } from "react-router-dom";

import unitModel from "../../lib/firebase/unitModel";
import { listTenants } from "../../lib/firebase/tenantModel";
import {
  getPaymentsByOwner,
  getPaymentStats,
  markPaymentAsPaid,
} from "../../lib/firebase/paymentModel";
import { sendReminder } from "../../lib/firebase/rentModel";
import repairModel from "../../lib/firebase/repairModel";
import PageHeader from "../../components/PageHeader";
import ImportPortfolioModal from "../../components/ImportPortfolioModal";
import RecentExpensesDemo from "../../components/RecentExpensesDemo";
import AddExpenseModal from "../../components/expense/AddExpenseModal";
import {
  createExpense,
  getExpensesByOwner,
} from "../../lib/firebase/expenseModel";
import { propertyKeys } from "../../lib/queryKeys";
import { getProperties } from "../../lib/api/propertiesApi";
import { listConnectAccounts } from "../../lib/payments/stripe";
import SettingsModel from "../../lib/firebase/settingsModel";
import { getInvestorMetrics } from "../../lib/api/investorMetricsApi";
import {
  getLeoPulse,
  getLeoRequiringAttention,
  getLeoAiTimeSaved,
} from "../../lib/api/leoApi";
import { getDecisionPointsCount } from "../../lib/api/maintenanceApi";
import pluralize from "pluralize";
import {
  getDebugMode,
  getDebugUnitCount,
  isLaunchDemoMode,
} from "../../helpers/constants";
import {
  buildGhostRepairs,
  buildGhostRentPayments,
} from "../../helpers/launchDemoGhostData";
import { db } from "../../helpers/firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import {
  getDebugUnitsExtended,
  getDebugTenants,
  getDebugPayments,
  getDebugDashboardStats,
  getAllDebugRepairs,
} from "../../helpers/debugData";

const { TextArea } = Input;
const { Option } = Select;

const SHOW_EXPENSES_DEMO = false; // flip to false or comment out to hide demo section

/** Set true to show Rent collection / Vendor bidding pills above Portfolio Health */
const SHOW_RENT_VENDOR_DASHBOARD_TABS = false;

const Dashboard = () => {
  const { isMobile } = useBreakpoints();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isLeaseModalVisible, setIsLeaseModalVisible] = useState(false);
  const [editLease, setEditLease] = useState(null);
  const [isRentModalVisible, setIsRentModalVisible] = useState(false);
  const [rentModalData, setRentModalData] = useState(null);
  const [isDelinquencyModalVisible, setIsDelinquencyModalVisible] =
    useState(false);

  const [isRentRollModalVisible, setIsRentRollModalVisible] = useState(false);
  const [isOwnerStatementsModalVisible, setIsOwnerStatementsModalVisible] =
    useState(false);
  const [isLeaseExpirationsModalVisible, setIsLeaseExpirationsModalVisible] =
    useState(false);
  const [isTaxSummariesModalVisible, setIsTaxSummariesModalVisible] =
    useState(false);
  const [isImportPortfolioModalOpen, setIsImportPortfolioModalOpen] =
    useState(false);
  const [isAddExpenseModalVisible, setIsAddExpenseModalVisible] =
    useState(false);
  const [isDscrInfoModalVisible, setIsDscrInfoModalVisible] = useState(false);
  const [dscrModalDebtService, setDscrModalDebtService] = useState(null); // number | null; synced when modal opens
  const [dscrModalSaving, setDscrModalSaving] = useState(false);
  const [expenseForm] = Form.useForm();

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();
  const debugUnitCount = getDebugUnitCount();

  // Optional: allow the "Getting started" modal to open the CSV import flow directly
  useEffect(() => {
    const shouldOpen = location?.state?.openImportPortfolioModal;
    if (!shouldOpen) return;
    setIsImportPortfolioModalOpen(true);
  }, [location?.state?.openImportPortfolioModal]);

  const { data: userSettings } = useQuery({
    queryKey: propertyKeys.settings(user?.uid),
    queryFn: () => SettingsModel.getOrCreateSettings(),
    enabled: !!user?.uid,
  });

  /**
   * Ghost portfolio only with `?debug=true`. Persisted pref can opt out (`false`);
   * unset/`true` uses sample data while debugging (see Settings when `?debug=true`).
   */
  const useDemoPortfolioData =
    isDebugMode && userSettings?.preferences?.demoModePortfolio !== false;
  const effectiveDebugUnitCount = debugUnitCount;

  // Load data using useQuery for better caching and performance
  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(
      user?.uid,
      useDemoPortfolioData,
      effectiveDebugUnitCount
    ),
    queryFn: () => {
      if (useDemoPortfolioData) {
        return Promise.resolve(getDebugUnitsExtended(effectiveDebugUnitCount));
      }
      return unitModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || useDemoPortfolioData,
  });

  const { data: tenantsData, isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(
      user?.uid,
      useDemoPortfolioData,
      effectiveDebugUnitCount
    ),
    queryFn: () => {
      if (useDemoPortfolioData) {
        // Pass unitCount so tenants match the occupied units (85% occupancy)
        return Promise.resolve({
          tenants: getDebugTenants(effectiveDebugUnitCount),
        });
      }
      return listTenants();
    },
    enabled: !!user?.uid || useDemoPortfolioData,
  });

  const {
    data: paymentsResult,
    isLoading: isPaymentsLoading,
    isFetching: isPaymentsFetching,
    error: paymentsError,
  } = useQuery({
    queryKey: propertyKeys.payments(
      user?.uid,
      useDemoPortfolioData,
      effectiveDebugUnitCount
    ),
    queryFn: () => {
      if (useDemoPortfolioData) {
        const debugPayments = getDebugPayments(effectiveDebugUnitCount);
        return Promise.resolve({
          success: true,
          payments: debugPayments.payments,
        });
      }
      return getPaymentsByOwner();
    },
    enabled: !!user?.uid || useDemoPortfolioData,
  });

  const {
    data: statsResult,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
    error: statsError,
  } = useQuery({
    queryKey: propertyKeys.paymentStats(
      user?.uid,
      useDemoPortfolioData,
      effectiveDebugUnitCount
    ),
    queryFn: () => {
      if (useDemoPortfolioData) {
        const debugPayments = getDebugPayments(effectiveDebugUnitCount);
        return Promise.resolve({ success: true, stats: debugPayments.summary });
      }
      return getPaymentStats();
    },
    enabled: !!user?.uid || useDemoPortfolioData,
  });

  const { data: repairsData, isLoading: isRepairsLoading } = useQuery({
    queryKey: propertyKeys.repairs(user?.uid, useDemoPortfolioData),
    queryFn: () => {
      if (useDemoPortfolioData) {
        return Promise.resolve(getAllDebugRepairs());
      }
      return repairModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || useDemoPortfolioData,
  });

  const nowForExpenses = new Date();
  const currentMonthStart = new Date(
    nowForExpenses.getFullYear(),
    nowForExpenses.getMonth(),
    1
  );
  const currentMonthEnd = new Date(
    nowForExpenses.getFullYear(),
    nowForExpenses.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
  const { data: expensesResult } = useQuery({
    queryKey: propertyKeys.expenses(user?.uid),
    queryFn: () =>
      getExpensesByOwner({
        startDate: currentMonthStart,
        endDate: currentMonthEnd,
      }),
    enabled: !!user?.uid,
  });
  const currentMonthExpenses = expensesResult?.success
    ? expensesResult.expenses || []
    : [];
  const totalExpenses = currentMonthExpenses.reduce(
    (sum, e) => sum + (Number(e.amount) || 0),
    0
  );

  const { data: investorMetrics } = useQuery({
    queryKey: ["investor-metrics", user?.uid],
    queryFn: getInvestorMetrics,
    enabled: !!user?.uid,
    staleTime: 60 * 1000,
  });

  /** null = follow server; boolean = optimistic until Firestore + refetch match */
  const [
    importSectionCollapsedOptimistic,
    setImportSectionCollapsedOptimistic,
  ] = useState(null);

  const importSectionCollapsedFromServer =
    !!userSettings?.preferences?.dashboardImportSectionCollapsed;
  const importSectionCollapsed =
    importSectionCollapsedOptimistic !== null
      ? importSectionCollapsedOptimistic
      : importSectionCollapsedFromServer;

  useEffect(() => {
    if (importSectionCollapsedOptimistic === null) return;
    if (importSectionCollapsedFromServer === importSectionCollapsedOptimistic) {
      setImportSectionCollapsedOptimistic(null);
    }
  }, [importSectionCollapsedFromServer, importSectionCollapsedOptimistic]);

  const persistImportSectionCollapsed = (collapsed) => {
    setImportSectionCollapsedOptimistic(collapsed);
    if (!user?.uid) return;
    (async () => {
      try {
        await SettingsModel.setDashboardImportSectionCollapsed(
          user.uid,
          collapsed
        );
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.settings(user.uid),
        });
      } catch (e) {
        setImportSectionCollapsedOptimistic(null);
        message.error("Could not save your preference.");
      }
    })();
  };

  const { data: leoPulseData } = useQuery({
    queryKey: ["leo", "pulse", user?.uid],
    queryFn: () => getLeoPulse(user?.uid),
    enabled: !!user?.uid,
    staleTime: 60 * 1000,
  });
  const leoPulseActions = leoPulseData?.actions ?? [];

  const { data: leoAttentionData } = useQuery({
    queryKey: ["leo", "requiring-attention", user?.uid],
    queryFn: () => getLeoRequiringAttention(user?.uid),
    enabled: !!user?.uid,
    staleTime: 60 * 1000,
  });
  const leoRequiringAttention = leoAttentionData?.actions ?? [];

  const { data: decisionPointsData } = useQuery({
    queryKey: ["maintenance", "decision-points-count", user?.uid],
    queryFn: getDecisionPointsCount,
    enabled: !!user?.uid,
    staleTime: 60 * 1000,
  });
  const leoDecisionPointsCount = decisionPointsData?.count ?? 0;
  const leoHoursSaved = decisionPointsData?.hoursSaved ?? 0;

  const { data: leoAiTimeSaved, isLoading: leoAiTimeSavedLoading } = useQuery({
    queryKey: ["leo", "ai-time-saved", user?.uid],
    queryFn: () => getLeoAiTimeSaved({ days: 90 }),
    enabled: !!user?.uid,
    staleTime: 60 * 1000,
  });

  // Load properties + connect accounts when Tax Summaries modal is open (for entity/LLC on exports)
  const { data: taxModalProperties = [] } = useQuery({
    queryKey: ["properties", "tax-modal", user?.uid],
    queryFn: async () => {
      try {
        const res = await getProperties();
        return res?.properties ?? [];
      } catch {
        return [];
      }
    },
    enabled: !!user?.uid && isTaxSummariesModalVisible,
  });
  const { data: taxModalConnectAccounts = [] } = useQuery({
    queryKey: ["connectAccounts", "tax-modal", user?.uid],
    queryFn: async () => {
      const res = await listConnectAccounts();
      return res?.accounts ?? [];
    },
    enabled: !!user?.uid && isTaxSummariesModalVisible,
  });

  // When DSCR modal opens, seed from user settings so user can edit and save right there
  useEffect(() => {
    if (
      isDscrInfoModalVisible &&
      userSettings?.portfolio?.monthlyDebtService != null
    ) {
      setDscrModalDebtService(userSettings.portfolio.monthlyDebtService);
    } else if (isDscrInfoModalVisible) {
      setDscrModalDebtService(null);
    }
  }, [isDscrInfoModalVisible, userSettings?.portfolio?.monthlyDebtService]);

  // Leo: Toast when a new Zillow lead is created (from leads collection)
  const seenLeadIdsRef = useRef(new Set());
  const initialLeadsLoadRef = useRef(false);
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "leads"),
      orderBy("createdAt", "desc"),
      limit(10)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      if (!initialLeadsLoadRef.current) {
        snapshot.docs.forEach((doc) => seenLeadIdsRef.current.add(doc.id));
        initialLeadsLoadRef.current = true;
        return;
      }
      snapshot.docChanges().forEach((change) => {
        if (change.type !== "added") return;
        const id = change.doc.id;
        const data = change.doc.data();
        if (seenLeadIdsRef.current.has(id)) return;
        seenLeadIdsRef.current.add(id);
        const name = data?.name || data?.email || "Unknown";
        message.success(
          `Leo AI: New Zillow Lead received from ${name}. Automated showing invite sent.`,
          6
        );
      });
    });
    return () => unsub();
  }, [user?.uid]);

  const debtServiceFromUnits = (units || []).reduce(
    (sum, u) => sum + (Number(u.mortgage?.monthlyPayment) || 0),
    0
  );
  const showDebtServicePlaceholder =
    (dscrModalDebtService == null || dscrModalDebtService === 0) &&
    debtServiceFromUnits > 0;

  const tenants = tenantsData?.tenants || [];
  const payments = paymentsResult?.success ? paymentsResult.payments || [] : [];
  const paymentStats = statsResult?.success
    ? statsResult.stats
    : {
        totalRent: 0,
        collectedRent: 0,
        pendingRent: 0,
        overdueRent: 0,
        collectionRate: 0,
      };
  const repairsRaw = repairsData || [];
  const isLoading =
    isUnitsLoading ||
    isTenantsLoading ||
    isPaymentsLoading ||
    isStatsLoading ||
    isRepairsLoading;

  // NOTE: We intentionally do NOT force-refetch on window focus here.
  // The global React Query defaults already prevent refetch-on-focus to reduce Firestore reads.

  // Process payments to automatically mark overdue payments (same logic as RentDashboard)
  let processedPayments = payments.map((payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    // Find tenant and unit information
    const tenant = tenants.find((t) => t.id === payment.tenantId);
    const unit = units.find((u) => u.id === payment.unitId);

    // If payment is pending and due date is before today, mark as overdue
    if (payment.status === "pending" && dueDate < today) {
      return {
        ...payment,
        status: "overdue",
        tenantName: tenant
          ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
            tenant.name
          : "Unknown Tenant",
        unitName: unit
          ? unit.name || getUnitDisplayAddress(unit)
          : "Unknown door",
      };
    }

    return {
      ...payment,
      tenantName: tenant
        ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
          tenant.name
        : "Unknown Tenant",
      unitName: unit
        ? unit.name || getUnitDisplayAddress(unit)
        : "Unknown door",
    };
  });

  const launchDemoDashboard =
    isLaunchDemoMode() && !useDemoPortfolioData && !!user?.uid;
  if (launchDemoDashboard && payments.length < 14) {
    processedPayments = [
      ...buildGhostRentPayments(user.uid, units.length || 30),
      ...processedPayments,
    ];
  }
  const repairs =
    launchDemoDashboard && repairsRaw.length < 8
      ? [...buildGhostRepairs(user.uid), ...repairsRaw]
      : repairsRaw;

  // Welcome notification and analytics (placed after computed metrics are defined)
  // This effect depends on computed values like totalRent and occupancyRate,
  // so it is intentionally located after those values are declared.

  const handleTabClick = (tab) => {
    // Track tab change
    trackTabChange(tab, "dashboard", {
      user_id: user?.uid,
      units_count: units.length,
      tenants_count: tenants.length,
    });

    // Navigate to appropriate page instead of changing tabs
    switch (tab) {
      case "rent":
        navigate("/rent");
        break;
      case "leases":
        navigate("/leases");
        break;
      case "repairs":
        navigate("/repairs");
        break;
      default:
        break;
    }
  };

  const handleSummaryCardClick = (type) => {
    trackCardClick(type, "dashboard", {
      user_id: user?.uid,
      units_count: units.length,
      tenants_count: tenants.length,
    });
  };

  const handleEmptyStateCTA = (type, action) => {
    trackEmptyStateAction(action, "dashboard", {
      type,
      user_id: user?.uid,
      units_count: units.length,
      tenants_count: tenants.length,
    });
  };

  const showLeaseModal = (lease = null) => {
    trackModalInteraction("lease", "opened", {
      user_id: user?.uid,
      is_edit: !!lease,
      lease_id: lease?.id,
    });
    setEditLease(lease);
    setIsLeaseModalVisible(true);
  };

  const handleLeaseModalClose = () => {
    trackModalInteraction("lease", "closed", {
      user_id: user?.uid,
      is_edit: !!editLease,
    });
    setIsLeaseModalVisible(false);
    setEditLease(null);
  };

  const handleLeaseModalSuccess = () => {
    trackFormSubmission("lease", true, {
      user_id: user?.uid,
      is_edit: !!editLease,
      lease_id: editLease?.id,
    });
    // Refresh leases data
    trackEvent("Lease Created/Updated", {
      user_id: user?.uid,
    });
  };

  const showRentModal = (type, itemId, itemName, currentRent) => {
    setRentModalData({ type, itemId, itemName, currentRent });
    setIsRentModalVisible(true);
  };

  const handleRentModalClose = () => {
    setIsRentModalVisible(false);
    setRentModalData(null);
  };

  const handleRentModalSuccess = () => {
    setIsRentModalVisible(false);
    setRentModalData(null);
    // Data will be automatically refreshed by useQuery
  };

  const showDelinquencyModal = () => {
    setIsDelinquencyModalVisible(true);
  };

  const handleDelinquencyModalClose = () => {
    setIsDelinquencyModalVisible(false);
  };

  const showRentRollModal = () => {
    setIsRentRollModalVisible(true);
  };

  const handleRentRollModalClose = () => {
    setIsRentRollModalVisible(false);
  };

  const showOwnerStatementsModal = () => {
    setIsOwnerStatementsModalVisible(true);
  };

  const handleOwnerStatementsModalClose = () => {
    setIsOwnerStatementsModalVisible(false);
  };

  const showTaxSummariesModal = () => {
    setIsTaxSummariesModalVisible(true);
  };

  const handleTaxSummariesModalClose = () => {
    setIsTaxSummariesModalVisible(false);
  };

  const showLeaseExpirationsModal = () => {
    setIsLeaseExpirationsModalVisible(true);
  };

  const handleLeaseExpirationsClose = () => {
    setIsLeaseExpirationsModalVisible(false);
  };

  const handleCardClick = (section, type) => {
    // Track the card click
    trackCardClick(type, section, {
      user_id: user?.uid,
      page: "dashboard",
      units_count: units.length,
      tenants_count: tenants.length,
    });

    // Navigate to appropriate page
    switch (section) {
      case "property":
        if (type === "rent") navigate("/rent");
        if (type === "occupancy") navigate("/units");
        if (type === "leases") showLeaseExpirationsModal(); // Open modal instead of navigating
        break;
      case "repairs":
        navigate(`/repairs?tab=${type}`);
        break;
      case "reports":
        if (type === "delinquency") {
          showDelinquencyModal();
        }
        if (type === "rentRoll") {
          showRentRollModal();
        }
        if (type === "statements") {
          showOwnerStatementsModal();
        }
        if (type === "taxSummaries") {
          showTaxSummariesModal();
        }
        break;
      default:
        break;
    }
  };

  const handleSendReminder = (payment) => {
    (async () => {
      try {
        const tenant = tenants.find((t) => t.id === payment.tenantId);
        // Pass paymentId when sending reminder for specific payment
        const result = await sendReminder(payment.tenantId, payment.id);
        if (result?.success) {
          const tenantName =
            result.data?.tenantName ||
            (tenant
              ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim()
              : "Tenant");
          message.success(`Rent reminder sent to ${tenantName}`);
          trackEvent("rent_reminder_sent", {
            payment_id: payment.id,
            tenant_id: payment.tenantId,
            tenant_name: tenantName,
            unit_id: payment.unitId,
            amount: payment.amount,
          });
        } else {
          message.error("Failed to send reminder");
        }
      } catch (err) {
        console.error("Error sending reminder:", err);
        // Display specific error messages
        if (
          err.message?.includes("Payment not found") ||
          err.message?.includes("does not belong")
        ) {
          message.error(err.message);
        } else {
          message.error("Failed to send reminder");
        }
      }
    })();
  };

  const triggerConfetti = () => {
    // LandlordOps brand colors
    const colors = ["#86E49D", "#A4F1B1", "#5DD77A", "#2C5F2D"];

    const duration = 2000; // 2 seconds (faster)
    const end = Date.now() + duration;

    const confettiInterval = setInterval(() => {
      if (Date.now() > end) {
        clearInterval(confettiInterval);
        return;
      }

      // Left side - angled to cross middle
      confetti({
        particleCount: 10,
        angle: 75,
        spread: 80,
        origin: { x: 0, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
        drift: 0.5, // Drift towards center
      });

      // Right side - angled to cross middle
      confetti({
        particleCount: 10,
        angle: 105,
        spread: 80,
        origin: { x: 1, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
        drift: -0.5, // Drift towards center
      });

      // Top center - wide spread
      confetti({
        particleCount: 15,
        angle: 90,
        spread: 90,
        origin: { x: 0.5, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });

      // Additional bursts from sides for more coverage
      confetti({
        particleCount: 8,
        angle: 60,
        spread: 70,
        origin: { x: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });

      confetti({
        particleCount: 8,
        angle: 120,
        spread: 70,
        origin: { x: 1 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });
    }, 100); // Faster interval - every 100ms instead of 250ms
  };

  const handleMarkPaid = (payment) => {
    (async () => {
      try {
        const res = await markPaymentAsPaid(payment.id, "manual");
        if (res?.success) {
          message.success("Payment marked as paid");
          triggerConfetti(); // Trigger confetti celebration
          trackEvent("payment_marked_paid", {
            payment_id: payment.id,
            tenant_id: payment.tenantId,
            unit_id: payment.unitId,
            amount: payment.amount,
          });
          queryClient.invalidateQueries({
            queryKey: propertyKeys.payments(user?.uid),
          });
          queryClient.invalidateQueries({
            queryKey: propertyKeys.paymentStats(user?.uid),
          });
        } else {
          message.error("Failed to mark payment as paid");
        }
      } catch (err) {
        console.error("Error marking payment as paid:", err);
        message.error("Failed to mark payment as paid");
      }
    })();
  };

  const handleGenerateStatement = () => {
    // TODO: Implement generate statement functionality
    if (process.env.NODE_ENV === "development") {
      console.log("Generate delinquency statement");
    }
  };

  const showRepairModal = () => {
    // For now, show a message that repair functionality is coming soon
    message.info("Repair management functionality is coming soon!");
  };

  // Calculate summary statistics from real data
  // Calculate total rent using currentRent (market rate) for units
  const totalUnitRent = units.reduce(
    (sum, unit) => sum + (unit.currentRent || unit.baseRent || 0),
    0
  );

  // Calculate total tenant rent (what tenants actually pay)
  const totalTenantRent = tenants.reduce(
    (sum, tenant) => sum + (tenant.monthlyRent || 0),
    0
  );

  // Calculate outstanding rent based on current month payments only
  // Outstanding rent = Total rent due (current month) - Total rent collected (current month)
  let totalRent, collectedRent;

  // Calculate current month range for filtering payments
  const now = new Date();
  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  // Filter payments to current month only (matching getPaymentStats logic)
  const currentMonthPayments = processedPayments.filter((payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    return dueDate >= currentMonth && dueDate < nextMonth;
  });

  // Calculate total rent due for current month (sum of all current month payments)
  const currentMonthTotalRent = currentMonthPayments.reduce(
    (sum, p) => sum + (p.amount || 0),
    0
  );

  // Calculate collected rent for current month (paid or succeeded; Stripe uses 'succeeded')
  const currentMonthCollectedRent = currentMonthPayments
    .filter((p) => {
      const s = (p?.status || "").toLowerCase();
      return s === "paid" || s === "succeeded";
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  // Use payment stats if available and they match current month calculation
  // Otherwise, use the calculated current month values
  if (paymentStats.totalRent > 0 && paymentStats.collectedRent >= 0) {
    // Use payment stats, but ensure they're consistent with current month payments
    totalRent = paymentStats.totalRent;
    collectedRent = paymentStats.collectedRent;

    // If payment stats don't match current month payments, prefer current month calculation
    // This handles cases where paymentStats might be stale or incorrect
    if (Math.abs(paymentStats.totalRent - currentMonthTotalRent) > 0.01) {
      totalRent = currentMonthTotalRent;
      collectedRent = currentMonthCollectedRent;
    }
  } else {
    // No payment stats available, use current month calculation
    totalRent = currentMonthTotalRent;
    collectedRent = currentMonthCollectedRent;
  }

  // If there are no current month payments, outstanding should be $0
  // (not negative from fallback calculations)
  if (currentMonthPayments.length === 0) {
    totalRent = 0;
    collectedRent = 0;
  }

  // Debug logging to help identify data inconsistencies
  if (process.env.NODE_ENV === "development") {
    console.log("Dashboard Rent Calculation Debug:", {
      totalRent,
      collectedRent,
      outstandingRent: totalRent - collectedRent,
      currentMonthTotalRent,
      currentMonthCollectedRent,
      currentMonthPaymentsCount: currentMonthPayments.length,
      currentMonthPaidPaymentsCount: currentMonthPayments.filter((p) => {
        const s = (p?.status || "").toLowerCase();
        return s === "paid" || s === "succeeded";
      }).length,
      currentMonthPendingPaymentsCount: currentMonthPayments.filter(
        (p) => p.status === "pending"
      ).length,
      currentMonthOverduePaymentsCount: currentMonthPayments.filter(
        (p) => p.status === "overdue"
      ).length,
      totalTenantRent,
      totalUnitRent,
      paymentStatsTotalRent: paymentStats.totalRent,
      paymentStatsCollectedRent: paymentStats.collectedRent,
      currentMonthStart: currentMonth.toISOString(),
      nextMonthStart: nextMonth.toISOString(),
      allPaymentsCount: processedPayments.length,
      allPaidPaymentsCount: processedPayments.filter((p) => {
        const s = (p?.status || "").toLowerCase();
        return s === "paid" || s === "succeeded";
      }).length,
      statsResultSuccess: statsResult?.success,
      paymentsResultSuccess: paymentsResult?.success,
      timestamp: new Date().toISOString(),
    });
  }

  if (collectedRent > totalRent && process.env.NODE_ENV === "development") {
    console.info(
      "Dashboard: Collected rent exceeds total rent (negative outstanding)",
      {
        totalRent,
        collectedRent,
        outstandingRent: totalRent - collectedRent,
        totalTenantRent,
        totalUnitRent,
        paymentStatsTotalRent: paymentStats.totalRent,
        paymentStatsCollectedRent: paymentStats.collectedRent,
        paidPaymentsCount: processedPayments.filter((p) => {
          const s = (p?.status || "").toLowerCase();
          return s === "paid" || s === "succeeded";
        }).length,
        note: "This may indicate overpayments, advance payments, or credits applied to accounts",
      }
    );
  }

  // Calculate metrics for Report Details
  const currentRentRoll = totalRent;

  // Debug logging for Rent Roll calculation
  if (process.env.NODE_ENV === "development") {
    console.log("Dashboard Rent Roll Calculation Debug:", {
      currentRentRoll: currentRentRoll,
      totalRent: totalRent,
      totalTenantRent: totalTenantRent,
      totalUnitRent: totalUnitRent,
      tenantsCount: tenants.length,
      unitsCount: units.length,
      tenantsWithRent: tenants.filter((t) => t.monthlyRent > 0).length,
      unitsWithRent: units.filter((u) => (u.currentRent || u.baseRent) > 0)
        .length,
      tenantsSample: tenants.slice(0, 3).map((t) => ({
        id: t.id,
        name: t.name,
        monthlyRent: t.monthlyRent,
        unitId: t.unitId,
      })),
      unitsSample: units.slice(0, 3).map((u) => ({
        id: u.id,
        name: u.name,
        currentRent: u.currentRent,
        baseRent: u.baseRent,
      })),
      timestamp: new Date().toISOString(),
    });
  }

  const totalUnits = units.length;

  const occupiedUnits = units.filter((unit) => {
    return tenants.some(
      (tenant) => tenant.unitId === unit.id && isTenantActive(tenant)
    );
  }).length;

  const occupancyRate =
    totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  // Welcome notification on first dashboard load and analytics
  useEffect(() => {
    // Track dashboard page view
    trackPageView("dashboard", {
      user_id: user?.uid,
      user_email: user?.email,
      units_count: units.length,
      tenants_count: tenants.length,
      repairs_count: repairs.length,
      total_rent: totalRent,
      occupancy_rate: occupancyRate,
    });

    // Track time on page when component unmounts
    const startTime = Date.now();
    return () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackTimeOnPage("dashboard", timeSpent);
    };
  }, [
    user,
    units.length,
    tenants.length,
    repairs.length,
    totalRent,
    occupancyRate,
  ]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "active":
      case "completed":
        return "success";
      case "overdue":
      case "expired":
        return "error";
      case "pending":
      case "in progress":
      case "scheduled":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "active":
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "overdue":
      case "expired":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "pending":
      case "in progress":
      case "scheduled":
        return <ClockCircleOutlined style={{ color: "#1890ff" }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    }
  };

  // Show skeleton loaders while data is loading
  if (isLoading) {
    return (
      <DashboardContainer className="dashboard-container">
        <PageHeader
          title="Dashboard"
          subtitle="Overview of your portfolio"
          icon={BarChartOutlined}
        />
        <DashboardContent>
          <SectionTitle>
            <SectionIcon $color="white">
              <HomeOutlined />
            </SectionIcon>
            Property Overview
          </SectionTitle>
          <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={8} key={i}>
                <OverviewCard>
                  <CardContent>
                    <Skeleton
                      active
                      avatar={{ shape: "square", size: 48 }}
                      paragraph={{ rows: 3 }}
                    />
                  </CardContent>
                </OverviewCard>
              </Col>
            ))}
          </Row>
          <SectionTitle>
            <SectionIcon $color="white">
              <ToolOutlined />
            </SectionIcon>
            Repair Management
          </SectionTitle>
          <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} sm={8} key={i}>
                <OverviewCard>
                  <CardContent>
                    <Skeleton
                      active
                      avatar={{ shape: "square", size: 48 }}
                      paragraph={{ rows: 3 }}
                    />
                  </CardContent>
                </OverviewCard>
              </Col>
            ))}
          </Row>
        </DashboardContent>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer className="dashboard-container">
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your portfolio"
        icon={BarChartOutlined}
      />

      <DashboardContent>
        {!useDemoPortfolioData && (
          <ImportDashboardSection>
            <ImportDashboardSectionHeader>
              <ImportDashboardSectionHeaderMain
                role={importSectionCollapsed ? "button" : undefined}
                tabIndex={importSectionCollapsed ? 0 : undefined}
                $clickable={importSectionCollapsed}
                onClick={
                  importSectionCollapsed
                    ? () => persistImportSectionCollapsed(false)
                    : undefined
                }
                onKeyDown={
                  importSectionCollapsed
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          persistImportSectionCollapsed(false);
                        }
                      }
                    : undefined
                }
              >
                <SectionIcon $color="white">
                  <CloudUploadOutlined />
                </SectionIcon>
                <ImportDashboardSectionTitleGroup
                  $collapsed={importSectionCollapsed}
                >
                  <ImportDashboardSectionTitleText>
                    Import your portfolio
                  </ImportDashboardSectionTitleText>
                  {importSectionCollapsed && (
                    <ImportDashboardSectionHint>
                      Click to expand · rent roll CSV (additive only, up to
                      2,000 rows)
                    </ImportDashboardSectionHint>
                  )}
                </ImportDashboardSectionTitleGroup>
              </ImportDashboardSectionHeaderMain>
              {importSectionCollapsed ? (
                <Tooltip title="Expand this section">
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      persistImportSectionCollapsed(false);
                    }}
                    aria-label="Expand import section"
                    style={{
                      color: "rgba(255, 255, 255, 0.95)",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Hide this section (reopen anytime from here)">
                  <Button
                    type="text"
                    icon={<MinusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      persistImportSectionCollapsed(true);
                    }}
                    aria-label="Minimize import section"
                    style={{
                      color: "rgba(255, 255, 255, 0.95)",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </ImportDashboardSectionHeader>
            <AnimatePresence initial={false}>
              {!importSectionCollapsed && (
                <ImportPortfolioPanelMotion
                  key="dashboard-import-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{
                    opacity: 0,
                    height: 0,
                    transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
                  }}
                  transition={{
                    duration: 0.32,
                    ease: [0.16, 1, 0.3, 1],
                    opacity: { duration: 0.22 },
                  }}
                >
                  <ImportPortfolioPanel>
                    <Row gutter={[24, 24]} align="middle">
                      <Col xs={24} lg={15}>
                        <ImportPortfolioHeading>
                          Bulk import from a rent roll CSV
                        </ImportPortfolioHeading>
                        <ImportPortfolioBody>
                          Adds new properties, doors, and tenants only. Nothing
                          is overwritten. Duplicate emails or doors are skipped.
                          Up to 2,000 rows per file. Map columns on the next
                          screen.
                        </ImportPortfolioBody>
                      </Col>
                      <Col xs={24} lg={9}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          icon={
                            <UploadOutlined
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          }
                          onClick={() => setIsImportPortfolioModalOpen(true)}
                          style={{
                            height: 48,
                            fontWeight: 600,
                            borderRadius: 12,
                            boxShadow: "0 4px 14px rgba(44, 95, 45, 0.35)",
                          }}
                        >
                          Start CSV import
                        </Button>
                        <ImportPortfolioFinePrint>
                          Run another import anytime from this section.
                        </ImportPortfolioFinePrint>
                      </Col>
                    </Row>
                  </ImportPortfolioPanel>
                </ImportPortfolioPanelMotion>
              )}
            </AnimatePresence>
          </ImportDashboardSection>
        )}
        <DashboardTabsWrapper>
          <OverviewTab
            units={units}
            tenants={tenants}
            rentPayments={processedPayments}
            repairs={repairs}
            totalRent={totalRent}
            collectedRent={collectedRent}
            totalExpenses={totalExpenses}
            investorMetrics={investorMetrics}
            userSettings={userSettings}
            occupancyRate={occupancyRate}
            showLeaseModal={showLeaseModal}
            showRentModal={showRentModal}
            showDelinquencyModal={showDelinquencyModal}
            showAddExpenseModal={() => setIsAddExpenseModalVisible(true)}
            handleCardClick={handleCardClick}
            currentRentRoll={currentRentRoll}
            navigate={navigate}
            isMobile={isMobile}
            onDscrCardClick={() => setIsDscrInfoModalVisible(true)}
            leoRequiringAttention={leoRequiringAttention}
            leoDecisionPointsCount={leoDecisionPointsCount}
            leoHoursSaved={leoHoursSaved}
            leoAiTimeSaved={leoAiTimeSaved}
            leoAiTimeSavedLoading={leoAiTimeSavedLoading}
            ownerId={user?.uid}
          />
          {SHOW_EXPENSES_DEMO && <RecentExpensesDemo />}
        </DashboardTabsWrapper>
      </DashboardContent>

      <LeaseModal
        isOpen={isLeaseModalVisible}
        onClose={handleLeaseModalClose}
        onSuccess={handleLeaseModalSuccess}
        editLease={editLease}
        ownerId={user?.uid}
      />

      <RentManagementModal
        isOpen={isRentModalVisible}
        onClose={handleRentModalClose}
        onSuccess={handleRentModalSuccess}
        type={rentModalData?.type}
        itemId={rentModalData?.itemId}
        itemName={rentModalData?.itemName}
        currentRent={rentModalData?.currentRent}
      />

      <DelinquencyReportModal
        isOpen={isDelinquencyModalVisible}
        onClose={handleDelinquencyModalClose}
        overduePayments={(processedPayments || []).filter(
          (p) => p.status === "overdue"
        )}
        totalDelinquent={(processedPayments || [])
          .filter((p) => p.status === "overdue")
          .reduce((sum, p) => sum + (p.amount || 0), 0)}
        onSendReminder={handleSendReminder}
        onMarkPaid={handleMarkPaid}
        onGenerateStatement={handleGenerateStatement}
        landlordId={user?.uid}
      />

      <RentRollReportModal
        isOpen={isRentRollModalVisible}
        onClose={handleRentRollModalClose}
        tenants={tenants || []}
        units={units || []}
        totalRentRoll={currentRentRoll}
      />

      <OwnerStatementsModal
        isOpen={isOwnerStatementsModalVisible}
        onClose={handleOwnerStatementsModalClose}
        tenants={tenants || []}
        units={units || []}
        payments={processedPayments || []}
        totalRent={totalRent}
        collectedRent={collectedRent}
        onGenerateStatement={handleGenerateStatement}
      />

      <TaxSummariesModal
        isOpen={isTaxSummariesModalVisible}
        onClose={handleTaxSummariesModalClose}
        payments={processedPayments || []}
        units={units || []}
        tenants={tenants || []}
        properties={taxModalProperties}
        connectAccounts={taxModalConnectAccounts}
      />

      <ImportPortfolioModal
        open={isImportPortfolioModalOpen}
        onClose={() => setIsImportPortfolioModalOpen(false)}
      />

      <StyledDscrModal
        title={
          <DscrModalHeader>
            <BarChartOutlined style={{ fontSize: 20 }} />
            <span>What is DSCR?</span>
          </DscrModalHeader>
        }
        open={isDscrInfoModalVisible}
        onCancel={() => setIsDscrInfoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDscrInfoModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={dscrModalSaving}
            onClick={async () => {
              if (!user?.uid) return;
              setDscrModalSaving(true);
              try {
                const value =
                  dscrModalDebtService != null && dscrModalDebtService >= 0
                    ? dscrModalDebtService
                    : null;
                await SettingsModel.updateSettings(user.uid, {
                  portfolio: {
                    ...(userSettings?.portfolio || {}),
                    monthlyDebtService: value,
                  },
                });
                // Refetch settings so the dashboard DSCR updates immediately with the new debt service
                await queryClient.refetchQueries({
                  queryKey: propertyKeys.settings(user.uid),
                });
                // Invalidate investor-metrics so next load uses new debt service from backend if applicable
                queryClient.invalidateQueries({
                  queryKey: ["investor-metrics", user.uid],
                });
                message.success(
                  value != null
                    ? "Debt service saved. DSCR will update."
                    : "Debt service cleared."
                );
                setIsDscrInfoModalVisible(false);
              } catch (e) {
                message.error("Failed to save");
              } finally {
                setDscrModalSaving(false);
              }
            }}
          >
            Save
          </Button>,
        ]}
        width={720}
      >
        <DscrModalSectionCard $variant="green">
          <DscrModalSectionTitleRow>
            <InfoCircleOutlined />
            <DscrModalSectionTitle>What is DSCR?</DscrModalSectionTitle>
          </DscrModalSectionTitleRow>
          <DscrModalBody>
            DSCR is your property’s income after expenses divided by your
            monthly loan payment. It tells you if the property can pay for
            itself.
          </DscrModalBody>
          <DscrModalBody>
            Example: $3,000 income after expenses and a $2,500 loan payment
            gives a DSCR of 1.2. That means you have a little extra. Under 1.0
            means the income doesn’t cover the payment.
          </DscrModalBody>
          <DscrModalBody>
            A good DSCR is at least 1.2 (most lenders want that for a loan or
            refi). Higher is better. 1.5 or more means more cushion.
          </DscrModalBody>
        </DscrModalSectionCard>

        <DscrModalSectionCard $variant="blue">
          <DscrModalSectionTitleRow>
            <FileTextOutlined />
            <DscrModalSectionTitle>FAQ</DscrModalSectionTitle>
          </DscrModalSectionTitleRow>
          <DscrModalCollapse
            accordion
            defaultActiveKey={[]}
            items={[
              {
                key: "1",
                label: "Are expenses included?",
                children:
                  "Yes. We use your collected rent minus expenses for this month. More expenses mean a lower number and a lower DSCR.",
              },
              {
                key: "2",
                label: "Multiple properties or multi-family?",
                children:
                  "Use the total you pay toward mortgages each month. One loan? Use that payment. Several loans or buildings? Add them all and enter the total here.",
              },
              {
                key: "3",
                label: "Can LandlordOps calculate it automatically?",
                children:
                  "Yes. Add the monthly mortgage payment on each door (Doors → edit door) and we'll add them up. Or type the total in the box below or in Settings → Portfolio.",
              },
              {
                key: "4",
                label: "Where do I find my monthly payment?",
                children:
                  "On your mortgage statement or on your lender's website. Use the main loan payment (principal and interest). You can include insurance and property taxes if you want. It's up to you.",
              },
            ]}
          />
        </DscrModalSectionCard>

        <DscrModalSectionCard $variant="gray">
          <DscrModalSectionTitleRow>
            <DollarOutlined />
            <DscrModalSectionTitle>
              Monthly debt service ($)
            </DscrModalSectionTitle>
          </DscrModalSectionTitleRow>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <InputNumber
              min={0}
              step={100}
              placeholder={
                showDebtServicePlaceholder
                  ? `Sum from doors: $${debtServiceFromUnits.toLocaleString(
                      "en-US"
                    )}`
                  : "e.g. 2,500"
              }
              value={dscrModalDebtService ?? undefined}
              onChange={(val) =>
                setDscrModalDebtService(val != null ? Number(val) : null)
              }
              formatter={(value) =>
                value != null ? `${Number(value).toLocaleString("en-US")}` : ""
              }
              parser={(displayValue) => {
                const str = String(displayValue || "").replace(/,/g, "");
                return str === "" ? "" : str;
              }}
              style={{ width: "100%", maxWidth: 220, borderRadius: 8 }}
            />
            {debtServiceFromUnits > 0 && (
              <DscrModalActionButton
                size="middle"
                onClick={() => setDscrModalDebtService(debtServiceFromUnits)}
              >
                Use sum from doors ($
                {debtServiceFromUnits.toLocaleString("en-US")})
              </DscrModalActionButton>
            )}
          </div>
          <DscrModalHint>
            Manual entry overwrites the sum from doors. You can also change this
            in Settings → Portfolio.
          </DscrModalHint>
        </DscrModalSectionCard>
      </StyledDscrModal>

      <AddExpenseModal
        open={isAddExpenseModalVisible}
        onCancel={() => {
          setIsAddExpenseModalVisible(false);
          expenseForm.resetFields();
        }}
        onSubmit={async (values) => {
          const result = await createExpense({
            date: values.date?.toDate?.() || values.date,
            payee: values.payee,
            amount: values.amount,
            unitId: values.unitId,
            category: values.category,
            description: values.description,
            receiptUrl: values.receiptUrl || "",
          });
          if (result.success) {
            message.success("Expense logged");
            setIsAddExpenseModalVisible(false);
            expenseForm.resetFields();
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.payments(user?.uid),
            });
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.expenses(user?.uid),
            });
          } else {
            message.error(result.error || "Failed to log expense");
          }
        }}
        units={units}
        form={expenseForm}
      />

      <Modal
        open={isLeaseExpirationsModalVisible}
        onCancel={handleLeaseExpirationsClose}
        title="Upcoming Rental Term Expirations"
        footer={[
          <Button key="close" onClick={handleLeaseExpirationsClose}>
            Close
          </Button>,
        ]}
        width={700}
      >
        <LeaseExpirationsContent>
          <Alert
            message="Rental Term Tracking"
            description="Track upcoming rental term expirations for your tenants. This helps you plan for lease renewals and rent adjustments."
            type="info"
            showIcon
            style={{ marginBottom: "1.5rem" }}
          />
          {(() => {
            const expiring = tenants
              .filter((t) => {
                if (!isTenantActive(t)) return false;
                const days = daysUntilLeaseEnd(t);
                return days > 0 && days <= 60;
              })
              .sort(
                (a, b) => (getLeaseEndDate(a) || 0) - (getLeaseEndDate(b) || 0)
              );

            return expiring.length === 0 ? (
              <EmptyState
                icon={<CalendarOutlined />}
                title="No Upcoming Expirations"
                description="No rental terms are expiring in the next 60 days."
              />
            ) : (
              <List
                dataSource={expiring}
                renderItem={(tenant) => {
                  const leaseEndDate = getLeaseEndDate(tenant);
                  const daysLeft = Math.ceil(daysUntilLeaseEnd(tenant));
                  const unit = units.find((u) => u.id === tenant.unitId);
                  return (
                    <List.Item>
                      <TenantExpirationCard>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "start",
                            width: "100%",
                          }}
                        >
                          <div>
                            <TenantName>
                              {tenant.firstName} {tenant.lastName}
                            </TenantName>
                            <TenantUnit>
                              {getUnitDisplayAddress(unit) ||
                                unit?.unitNumber ||
                                "Unknown door"}
                            </TenantUnit>
                            <ExpirationDate>
                              Expires:{" "}
                              {leaseEndDate?.toLocaleDateString() || "N/A"}
                            </ExpirationDate>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <DaysRemaining $urgent={daysLeft <= 30}>
                              {daysLeft} {daysLeft === 1 ? "day" : "days"}
                            </DaysRemaining>
                            <MonthlyRent>
                              ${tenant.monthlyRent?.toLocaleString() || "0"}/mo
                            </MonthlyRent>
                          </div>
                        </div>
                      </TenantExpirationCard>
                    </List.Item>
                  );
                }}
              />
            );
          })()}
        </LeaseExpirationsContent>
      </Modal>
    </DashboardContainer>
  );
};

const LEO_CYCLE_MS = 4500;
function LeoActivityCycler({ items }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!items?.length) return;
    const t = setInterval(
      () => setIndex((i) => (i + 1) % items.length),
      LEO_CYCLE_MS
    );
    return () => clearInterval(t);
  }, [items?.length]);
  if (!items?.length) return null;
  return (
    <span style={{ color: "rgba(255,255,255,0.95)" }}>{items[index]}</span>
  );
}

const OverviewTab = ({
  units,
  tenants,
  rentPayments,
  repairs,
  totalRent,
  collectedRent,
  totalExpenses = 0,
  investorMetrics,
  userSettings,
  occupancyRate,
  showLeaseModal,
  showRentModal,
  showDelinquencyModal,
  showAddExpenseModal,
  handleCardClick,
  currentRentRoll,
  navigate,
  isMobile,
  onDscrCardClick,
  leoRequiringAttention = [],
  leoDecisionPointsCount = 0,
  leoHoursSaved = 0,
  leoAiTimeSaved = null,
  leoAiTimeSavedLoading = false,
  ownerId,
}) => {
  const { userInfo } = useContext(AuthContext);
  const lockOs = shouldShowScaleUpgradeForFullOs(userInfo);
  const [scaleUpgradeOpen, setScaleUpgradeOpen] = useState(false);

  const localNoi = collectedRent - totalExpenses;
  // Use API NOI only when it's a positive number; when API returns 0 (e.g. backend has no rent data), use local so the card shows correct collected rent − expenses
  const noi =
    typeof investorMetrics?.noi === "number" && investorMetrics.noi > 0
      ? investorMetrics.noi
      : localNoi;
  // Expected NOI when nothing collected yet: use total rent due minus expenses so DSCR isn't 0
  const expectedNoi =
    typeof totalRent === "number" && typeof totalExpenses === "number"
      ? totalRent - totalExpenses
      : null;
  // DSCR: prefer backend; when backend does not return dscr, compute from NOI and debt service (Settings or sum from units)
  const apiDscr = investorMetrics?.dscr;
  const apiDebtService = investorMetrics?.debtService;
  const debtServiceFromUnits =
    units?.reduce(
      (sum, u) => sum + (Number(u.mortgage?.monthlyPayment) || 0),
      0
    ) || 0;
  const rawSettingsDebt = userSettings?.portfolio?.monthlyDebtService;
  const settingsDebtService =
    typeof rawSettingsDebt === "number" && rawSettingsDebt >= 0
      ? rawSettingsDebt
      : typeof rawSettingsDebt === "string" &&
        rawSettingsDebt !== "" &&
        !Number.isNaN(Number(rawSettingsDebt))
      ? Number(rawSettingsDebt)
      : undefined;
  const debtService =
    typeof apiDebtService === "number" && apiDebtService > 0
      ? apiDebtService
      : typeof settingsDebtService === "number" && settingsDebtService > 0
      ? settingsDebtService
      : debtServiceFromUnits > 0
      ? debtServiceFromUnits
      : undefined;
  // Use backend DSCR when present and positive; else compute locally. When API returns 0 (e.g. backend NOI is 0), fall back to local so we don't overwrite a valid expected-NOI-based DSCR and cause a "flash then 0" bug.
  const noiForDscr =
    typeof noi === "number" && noi > 0
      ? noi
      : expectedNoi != null && expectedNoi > 0
      ? expectedNoi
      : noi;
  const localDscr =
    noiForDscr != null && debtService != null && debtService > 0
      ? noiForDscr / debtService
      : undefined;
  const displayDscr =
    typeof apiDscr === "number" && apiDscr > 0
      ? apiDscr
      : localDscr != null && localDscr > 0
      ? localDscr
      : typeof apiDscr === "number"
      ? apiDscr
      : localDscr;
  const showDscrPlaceholder = displayDscr == null || displayDscr === undefined;
  const dscrAmber = typeof displayDscr === "number" && displayDscr < 1.2;
  // On desktop: show tooltip on hover. On mobile: show tooltip text on the card (tooltips don't work on touch)
  const ConditionalTooltip = ({ title, children }) => {
    if (isMobile && title) {
      const card = React.Children.only(children);
      const cardChildren = React.Children.toArray(card.props.children);
      return React.cloneElement(
        card,
        {},
        ...cardChildren,
        <MobileCardHint key="mobile-hint">{title}</MobileCardHint>
      );
    }
    return (
      <Tooltip title={title} className="dashboard-tooltip">
        {children}
      </Tooltip>
    );
  };

  // Calculate metrics for Property Overview
  // Allow negative values for overpayments, credits, and refunds
  const totalOutstandingRent = totalRent - collectedRent;
  const overdueUnits = rentPayments.filter(
    (p) => p.status === "overdue"
  ).length;

  const occupiedUnitsCount = units.filter((unit) => {
    return tenants.some(
      (tenant) => tenant.unitId === unit.id && isTenantActive(tenant)
    );
  }).length;

  const vacantUnitsCount = units.length - occupiedUnitsCount;
  const occupancyPercentage =
    units.length > 0
      ? Math.round((occupiedUnitsCount / units.length) * 100)
      : 0;

  const leasesExpiringSoon = tenants.filter((t) => {
    if (!isTenantActive(t)) return false;
    const days = daysUntilLeaseEnd(t);
    return days > 0 && days <= 60;
  }).length;

  // Calculate metrics for Repair Management
  // Match the logic from Repairs.jsx: active includes in_progress, open, assigned, pending
  const activeRepairsCount = repairs.filter((r) => {
    const status = r.status?.toLowerCase();
    return (
      status === "in_progress" ||
      status === "open" ||
      status === "assigned" ||
      status === "pending"
    );
  }).length;

  const highPriorityRepairs = repairs.filter((r) => {
    const status = r.status?.toLowerCase();
    const isActive =
      status === "in_progress" ||
      status === "open" ||
      status === "assigned" ||
      status === "pending";
    const isHighPriority = r.priority === "high" || r.priority === "urgent";
    return isActive && isHighPriority;
  }).length;

  const scheduledRepairsCount = repairs.filter((r) => {
    const status = r.status?.toLowerCase();
    return status === "scheduled";
  }).length;

  // Filter completed repairs within last 90 days
  // Only count tickets that have a completedDate field (no fallback to updatedAt)
  const now = new Date();
  now.setHours(23, 59, 59, 999); // End of today for comparison
  const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  ninetyDaysAgo.setHours(0, 0, 0, 0); // Start of day 90 days ago

  // Get all completed repairs first for debugging
  const allCompletedRepairs = repairs.filter((r) => {
    const status = r.status?.toLowerCase();
    return status === "completed" || r.completed === true;
  });

  const completedRepairsWithin90Days = allCompletedRepairs.filter((r) => {
    // Only use completedDate - don't fallback to updatedAt to avoid counting non-completed tickets
    if (!r.completedDate) {
      return false; // Skip tickets without completedDate
    }

    let completedDate = null;
    // Handle Firestore Timestamp
    if (typeof r.completedDate?.toDate === "function") {
      completedDate = r.completedDate.toDate();
    }
    // Handle JavaScript Date object
    else if (r.completedDate instanceof Date) {
      completedDate = r.completedDate;
    }
    // Handle string or number (timestamp)
    else {
      completedDate = new Date(r.completedDate);
    }

    if (!completedDate || isNaN(completedDate.getTime())) {
      return false; // Invalid date
    }

    // Only count if completedDate is within the last 90 days and not in the future
    const isWithin90Days = completedDate >= ninetyDaysAgo;
    const isNotFuture = completedDate <= now;

    return isWithin90Days && isNotFuture;
  });

  // Debug logging (remove in production if needed)
  if (process.env.NODE_ENV === "development") {
    console.log("Repair counts debug:", {
      totalRepairs: repairs.length,
      allCompleted: allCompletedRepairs.length,
      completedWithDate: allCompletedRepairs.filter((r) => r.completedDate)
        .length,
      completedWithin90Days: completedRepairsWithin90Days.length,
      ninetyDaysAgo: ninetyDaysAgo.toISOString(),
      now: now.toISOString(),
      sampleCompleted: allCompletedRepairs.slice(0, 3).map((r) => ({
        id: r.id,
        status: r.status,
        completed: r.completed,
        completedDate: r.completedDate,
        title: r.title?.substring(0, 30),
      })),
    });
  }

  const completedRepairsCount = completedRepairsWithin90Days.length;

  // Bank-Ready Collateral: properties with DSCR > 1.25 and 0 Urgent maintenance tickets (for lenders)
  const urgentRepairs = repairs.filter((r) => {
    const p = (r.priority || "").toString().toLowerCase();
    return p === "urgent" || p === "high";
  });
  const propertyIdsWithUrgent = new Set(
    urgentRepairs.map((r) => r.propertyId).filter(Boolean)
  );
  const allPropertyIds = [
    ...new Set(units.map((u) => u.propertyId).filter(Boolean)),
  ];
  const bankReadyPropertyIds = allPropertyIds.filter(
    (pid) => !propertyIdsWithUrgent.has(pid)
  );
  const bankReadyCount = bankReadyPropertyIds.length;
  const isPortfolioDscrHealthy =
    typeof displayDscr === "number" && displayDscr > 1.25;

  // Use actual counts instead of hardcoded 0
  const displayActiveRepairs = activeRepairsCount;
  const displayScheduledRepairs = scheduledRepairsCount;
  const displayCompletedRepairs = completedRepairsCount;

  const totalDelinquent = rentPayments
    .filter((p) => p.status === "overdue")
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const ownerStatementsGenerated = 0; // TODO: Implement when reports model is available

  const QUICK_START_FAQ = [
    {
      title: "You are always in control",
      description:
        "You are the CEO. Leo AI is your Autonomous Asset Manager: it only does what you allow. Set a Repair Auto-Approve Limit per door in Settings → Door settings (or leave it at $0 for manual approval on every bid). Leo AI will only schedule work that falls within your specific budget. Nothing above your limit is ever auto-approved.",
    },
    {
      title: "Where can I see what Leo AI did?",
      description:
        "Use Leo AI in the main navigation to see everything Leo AI has done on your behalf: auto-approved bids, rent reminders, maintenance coordination, and more. All activity is logged there so you stay fully in the loop.",
    },
    {
      title: "How do I set the Repair Auto-Approve Limit?",
      description:
        'Go to Settings → Door settings. For each door, you’ll see "AI Auto-Approve Limit ($)". Enter the maximum bid amount (in dollars) you’re comfortable having Leo AI accept and schedule without your approval. Leave it empty or at $0 for manual approval on every bid. Leo AI only schedules work within this budget.',
    },
    {
      title: "Collect rent and track portfolio yield",
      description:
        "Leo AI generates upcoming rent payments, sends reminders, applies late fees, and syncs paid/overdue statuses. Your portfolio yield, NOI, and DSCR are in Portfolio Health above. Use Reports for delinquency, owner statements, and tax-ready exports, all organized by lease, door, and tenant history.",
    },
    {
      title: "Coordinate maintenance within your budget",
      description:
        "Leo AI turns tenant messages into repair tickets, assigns your preferred vendors, and coordinates entry. Bids at or below your Auto-Approve Limit for that door get scheduled automatically. The rest wait for your approval. You stay in control. Leo AI executes.",
    },
    {
      title: "Stay audit-ready",
      description:
        "Generate portfolio yield reports, delinquency, owner statements, and tax-ready exports with one click. Everything is organized by lease, door, and tenant history.",
    },
  ];

  return (
    <div>
      {/* Portfolio Health (Investor Insight) */}
      <SectionTitle>
        <SectionIcon $color="white">
          <RiseOutlined />
        </SectionIcon>
        Portfolio Health
      </SectionTitle>
      {SHOW_RENT_VENDOR_DASHBOARD_TABS ? (
        <OsWorkspaceTabs
          role="tablist"
          aria-label="Rent collection and vendor bidding"
        >
          <OsWorkspaceTab
            type="button"
            role="tab"
            onClick={() =>
              lockOs ? setScaleUpgradeOpen(true) : navigate("/rent")
            }
          >
            {lockOs ? (
              <LockOutlined aria-hidden style={{ marginRight: 6 }} />
            ) : null}
            Rent collection
          </OsWorkspaceTab>
          <OsWorkspaceTab
            type="button"
            role="tab"
            onClick={() =>
              lockOs ? setScaleUpgradeOpen(true) : navigate("/bids")
            }
          >
            {lockOs ? (
              <LockOutlined aria-hidden style={{ marginRight: 6 }} />
            ) : null}
            Vendor bidding
          </OsWorkspaceTab>
        </OsWorkspaceTabs>
      ) : null}
      <Row gutter={[24, 24]} style={{ marginBottom: "1.25rem" }}>
        <Col xs={24}>
          <SilenceMetricsCard
            onClick={() => navigate("/leo")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate("/leo");
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="View Leo AI automation and AI time saved"
          >
            {leoAiTimeSavedLoading ? (
              <SilenceMetricsRow>
                <Skeleton
                  active
                  paragraph={{ rows: 1 }}
                  title={{ width: "50%" }}
                  style={{ flex: 1 }}
                />
                <Skeleton.Button active style={{ width: 120, height: 48 }} />
              </SilenceMetricsRow>
            ) : (
              <SilenceMetricsRow>
                <SilenceMetricsLeft>
                  <SilenceMetricsHeader>
                    <SilenceMetricsIconWrap>
                      <ThunderboltOutlined
                        aria-hidden
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    </SilenceMetricsIconWrap>
                    <div>
                      <SilenceMetricsLabel>Silence metrics</SilenceMetricsLabel>
                      <SilenceMetricsSub>
                        AI time saved from logged Leo AI actions
                      </SilenceMetricsSub>
                    </div>
                  </SilenceMetricsHeader>
                </SilenceMetricsLeft>
                <SilenceMetricsRight>
                  <SilenceMetricsValue>
                    {leoAiTimeSaved?.totalMinutes > 0
                      ? leoAiTimeSaved.totalHours >= 1
                        ? `${leoAiTimeSaved.totalHours} hrs`
                        : `${Math.round(leoAiTimeSaved.totalMinutes)} min`
                      : "-"}
                  </SilenceMetricsValue>
                  <SilenceMetricsFoot>
                    {leoAiTimeSaved?.actionCount
                      ? `${leoAiTimeSaved.actionCount} action${
                          leoAiTimeSaved.actionCount === 1 ? "" : "s"
                        } with time saved · last ${
                          leoAiTimeSaved.daysBack ?? 90
                        } days`
                      : "As Leo AI logs automation with time estimates, your total appears here."}
                  </SilenceMetricsFoot>
                </SilenceMetricsRight>
              </SilenceMetricsRow>
            )}
          </SilenceMetricsCard>
        </Col>
      </Row>
      {/* Guardian high-priority alerts (DSCR / Refi-Ready) */}
      {leoRequiringAttention.filter(
        (a) =>
          a.actionType === "dscr_attention" ||
          a.actionType === "refinancing_recommended" ||
          a.status === "requires_attention"
      ).length > 0 && (
        <Row gutter={[24, 24]} style={{ marginBottom: "1rem" }}>
          {leoRequiringAttention
            .filter(
              (a) =>
                a.actionType === "dscr_attention" ||
                a.actionType === "refinancing_recommended" ||
                a.status === "requires_attention"
            )
            .slice(0, 4)
            .map((a) => (
              <Col xs={24} md={12} key={a.id}>
                <Alert
                  type={
                    a.actionType === "refinancing_recommended"
                      ? "success"
                      : "warning"
                  }
                  showIcon
                  message={a.title}
                  description={a.description}
                  style={{ marginBottom: 0 }}
                />
              </Col>
            ))}
        </Row>
      )}
      {(leoDecisionPointsCount > 0 || leoHoursSaved > 0) && (
        <Row gutter={[24, 24]} style={{ marginBottom: "1rem" }}>
          <Col xs={24}>
            <LeftAlignAlertWrapper>
              <Alert
                type="info"
                showIcon
                icon={<BulbOutlined />}
                message="Cognitive Load Saved"
                description={`This month, Leo AI handled ${leoDecisionPointsCount} decision point${
                  leoDecisionPointsCount === 1 ? "" : "s"
                } for you. You saved ${leoHoursSaved} hour${
                  leoHoursSaved === 1 ? "" : "s"
                } of firefighting.`}
                style={{ marginBottom: 0 }}
              />
            </LeftAlignAlertWrapper>
          </Col>
        </Row>
      )}
      {isPortfolioDscrHealthy && (
        <Row gutter={[24, 24]} style={{ marginBottom: "1rem" }}>
          <Col xs={24}>
            <LeftAlignAlertWrapper>
              <Alert
                type="success"
                showIcon
                message="Portfolio Health for Lenders"
                description={
                  bankReadyCount > 0
                    ? `${bankReadyCount} propert${
                        bankReadyCount === 1 ? "y" : "ies"
                      } with DSCR > 1.25 and zero urgent maintenance. Bank-ready collateral for cash-out refi.`
                    : "DSCR is above 1.25. No properties have zero urgent tickets yet."
                }
                style={{ marginBottom: 0 }}
              />
            </LeftAlignAlertWrapper>
          </Col>
        </Row>
      )}
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24} md={12}>
          <ConditionalTooltip title="Net Operating Income: collected rent minus expenses this month.">
            <OverviewCard
              onClick={() =>
                lockOs ? setScaleUpgradeOpen(true) : navigate("/rent")
              }
              $variant="rent"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="rent">
                  <AccountBookOutlined />
                </CardIcon>
                <CardNumber>
                  $
                  {noi.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </CardNumber>
                <CardTitle>NOI (this month)</CardTitle>
                <CardSubtitle>Collected rent − expenses</CardSubtitle>
                <CardStatus $status="info">
                  {typeof investorMetrics?.noi === "number" &&
                  investorMetrics.noi > 0
                    ? "From investor metrics"
                    : "This month"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} md={12}>
          <ConditionalTooltip
            title={
              showDscrPlaceholder
                ? "DSCR = NOI ÷ monthly debt service. Click to learn how to add debt service."
                : "Debt service coverage ratio. Lenders typically require ≥ 1.2."
            }
          >
            <OverviewCard
              onClick={onDscrCardClick}
              $variant="occupancy"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="occupancy">
                  <BarChartOutlined />
                </CardIcon>
                {showDscrPlaceholder ? (
                  <CardNumber style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                    Add Debt Service to calculate DSCR.
                  </CardNumber>
                ) : (
                  <CardNumber
                    style={dscrAmber ? { color: "#d48806" } : undefined}
                  >
                    {typeof displayDscr === "number"
                      ? displayDscr.toFixed(2)
                      : "-"}
                  </CardNumber>
                )}
                <CardTitle>DSCR</CardTitle>
                <CardSubtitle>
                  {showDscrPlaceholder
                    ? "Debt service required"
                    : "Debt service coverage"}
                </CardSubtitle>
                <CardStatus $status={dscrAmber ? "warning" : "info"}>
                  {dscrAmber
                    ? "Below 1.2"
                    : typeof displayDscr === "number"
                    ? "Live"
                    : "-"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
      </Row>

      {/* Property Overview Section */}
      <SectionTitle>
        <SectionIcon $color="white">
          <HomeOutlined />
        </SectionIcon>
        Property Overview
      </SectionTitle>
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="Rent still due this month (total due minus collected). Negative = overpayments or credits.">
            <OverviewCard
              onClick={() => handleCardClick("property", "rent")}
              $variant="rent"
              $priority={overdueUnits > 0 ? "high" : "normal"}
            >
              <CardContent>
                <CardIcon $variant="rent">
                  <DollarOutlined />
                </CardIcon>
                <CardNumber>
                  ${totalOutstandingRent.toLocaleString()}
                </CardNumber>
                <CardTitle>Rent Still Due</CardTitle>
                <CardSubtitle>
                  {overdueUnits > 0
                    ? `${overdueUnits} ${pluralize(
                        "door",
                        overdueUnits
                      )} overdue`
                    : "All payments current"}
                </CardSubtitle>
                <CardStatus $status={overdueUnits > 0 ? "warning" : "success"}>
                  {overdueUnits > 0 ? "Overdue" : "All Paid"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="View door occupancy details and manage vacant doors">
            <OverviewCard
              onClick={() => handleCardClick("property", "occupancy")}
              $variant="occupancy"
              $priority={
                occupancyPercentage < 50
                  ? "high"
                  : occupancyPercentage < 80
                  ? "medium"
                  : "normal"
              }
            >
              <CardContent>
                <CardIcon $variant="occupancy">
                  <ApartmentOutlined />
                </CardIcon>
                <CardNumber>
                  {occupiedUnitsCount}/{units.length}
                </CardNumber>
                <CardTitle>
                  Occupied {pluralize("Door", occupiedUnitsCount)}
                </CardTitle>
                <CardSubtitle>{occupancyPercentage}% Occupancy</CardSubtitle>
                <CardStatus
                  $status={
                    occupancyPercentage >= 90
                      ? "success"
                      : occupancyPercentage >= 70
                      ? "warning"
                      : "error"
                  }
                >
                  {vacantUnitsCount} Vacant
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="Manage lease agreements and track upcoming renewals">
            <OverviewCard
              onClick={() => handleCardClick("property", "leases")}
              $variant="leases"
              $priority={
                leasesExpiringSoon > 10
                  ? "high"
                  : leasesExpiringSoon > 5
                  ? "medium"
                  : "normal"
              }
            >
              <CardContent>
                <CardIcon $variant="leases">
                  <CalendarOutlined />
                </CardIcon>
                <CardNumber>{leasesExpiringSoon}</CardNumber>
                <CardTitle>
                  Lease {pluralize("Expiration", leasesExpiringSoon)}
                </CardTitle>
                <CardSubtitle>In next 60 days</CardSubtitle>
                <CardStatus
                  $status={leasesExpiringSoon > 5 ? "warning" : "info"}
                >
                  Upcoming Renewals
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
      </Row>

      {/* Repair Management Section */}
      <SectionTitle>
        <SectionIcon $color="white">
          <ToolOutlined />
        </SectionIcon>
        Repair Management
      </SectionTitle>
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="View active repair requests and urgent maintenance issues">
            <OverviewCard
              onClick={() => handleCardClick("repairs", "active")}
              $variant="repairs"
              $priority={highPriorityRepairs > 0 ? "high" : "normal"}
            >
              <CardContent>
                <CardIcon $variant="repairs">
                  <AlertOutlined />
                </CardIcon>
                <CardNumber>{displayActiveRepairs}</CardNumber>
                <CardTitle>
                  Active {pluralize("Repair", displayActiveRepairs)}
                </CardTitle>
                <CardSubtitle>
                  {highPriorityRepairs > 0
                    ? `${highPriorityRepairs} High Priority`
                    : "All routine"}
                </CardSubtitle>
                <CardStatus
                  $status={highPriorityRepairs > 0 ? "warning" : "info"}
                >
                  {highPriorityRepairs > 0
                    ? "Attention Needed"
                    : "Under Control"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="View scheduled maintenance and upcoming repair appointments">
            <OverviewCard
              onClick={() => handleCardClick("repairs", "scheduled")}
              $variant="repairs"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="repairs">
                  <ScheduleOutlined />
                </CardIcon>
                <CardNumber>{displayScheduledRepairs}</CardNumber>
                <CardTitle>Scheduled</CardTitle>
                <CardSubtitle>This week</CardSubtitle>
                <CardStatus $status="info">On Track</CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={8}>
          <ConditionalTooltip title="View completed repairs and maintenance history">
            <OverviewCard
              onClick={() => handleCardClick("repairs", "completed")}
              $variant="repairs"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="repairs">
                  <CheckIcon />
                </CardIcon>
                <CardNumber>{displayCompletedRepairs}</CardNumber>
                <CardTitle>Completed</CardTitle>
                <CardSubtitle>Last 90 days</CardSubtitle>
                <CardStatus
                  $status={displayCompletedRepairs > 0 ? "success" : "info"}
                >
                  {displayCompletedRepairs > 0
                    ? "Great Progress"
                    : "Not Applicable"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
      </Row>

      {/* Report Details Section */}
      <SectionTitle>
        <SectionIcon $color="white">
          <BarChartOutlined />
        </SectionIcon>
        Report Details
      </SectionTitle>
      <ReportDetailsRow gutter={[24, 24]}>
        <Col xs={24} sm={12}>
          <ConditionalTooltip title="View portfolio yield report and monthly income analysis">
            <OverviewCard
              onClick={() => handleCardClick("reports", "rentRoll")}
              $variant="reports"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="reports">
                  <RiseOutlined />
                </CardIcon>
                <CardNumber>${currentRentRoll.toLocaleString()}</CardNumber>
                <CardTitle>Portfolio Yield</CardTitle>
                <CardSubtitle>Current monthly rent</CardSubtitle>
                <CardStatus $status="info">View Report</CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={12}>
          <ConditionalTooltip title="View delinquency report and outstanding payment details">
            <OverviewCard
              onClick={() => handleCardClick("reports", "delinquency")}
              $variant="reports"
              $priority={totalDelinquent > 0 ? "high" : "normal"}
            >
              <CardContent>
                <CardIcon $variant="reports">
                  <WarningOutlined />
                </CardIcon>
                <CardNumber>${totalDelinquent.toLocaleString()}</CardNumber>
                <CardTitle>Delinquency</CardTitle>
                <CardSubtitle>Outstanding amounts</CardSubtitle>
                <CardStatus
                  $status={totalDelinquent > 0 ? "warning" : "success"}
                >
                  {totalDelinquent > 0 ? "Action Required" : "All Current"}
                </CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={12}>
          <ConditionalTooltip title="Generate and view owner statements and financial reports">
            <OverviewCard
              onClick={() => handleCardClick("reports", "statements")}
              $variant="reports"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="reports">
                  <FileTextOutlined />
                </CardIcon>
                <CardNumber>{ownerStatementsGenerated}</CardNumber>
                <CardTitle>
                  Owner {pluralize("Statement", ownerStatementsGenerated)}
                </CardTitle>
                <CardSubtitle>Last month</CardSubtitle>
                <CardStatus $status="info">Generate New</CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={12}>
          <ConditionalTooltip title="Tax summaries and Schedule E export for tax season">
            <OverviewCard
              onClick={() => handleCardClick("reports", "taxSummaries")}
              $variant="reports"
              $priority="normal"
            >
              <CardContent>
                <CardIcon $variant="reports">
                  <AccountBookOutlined />
                </CardIcon>
                <CardTitle>Tax Summaries</CardTitle>
                <CardSubtitle>Schedule E export</CardSubtitle>
                <CardStatus $status="info">View & Export</CardStatus>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
        <Col xs={24} sm={12}>
          <ConditionalTooltip title="Generate a formal PDF for lenders: executive summary, T12, and Portfolio Yield">
            <OverviewCard
              $variant="reports"
              $priority="normal"
              style={{ cursor: "default" }}
            >
              <CardContent>
                <CardIcon $variant="reports">
                  <FilePdfOutlined />
                </CardIcon>
                <CardTitle>Lender Packet</CardTitle>
                <CardSubtitle>Executive summary & T12 PDF</CardSubtitle>
                <div style={{ marginTop: 12 }}>
                  <GenerateLenderPacketButton type="primary" size="middle" />
                </div>
              </CardContent>
            </OverviewCard>
          </ConditionalTooltip>
        </Col>
      </ReportDetailsRow>

      <SectionSeparator />

      {/*
 <FAQSectionWrapper>
 <FAQCard
 title={
 <FAQHeader>
 <FAQHeaderIcon>
 <img
  src="/icons/leo/leo.png"
  alt="Leo AI"
  style={{
  width: "24px",
  height: "24px",
  filter: "brightness(0) invert(1)",
  }}
 />
 </FAQHeaderIcon>
 <span>LandlordOps & Leo AI Quick-Start FAQ</span>
 </FAQHeader>
 }
 style={{ borderRadius: "16px", position: "relative" }}
 >
 <FAQSubtitle>
 Need a refresher on what's possible? Here are the biggest wins you
 can unlock today.
 </FAQSubtitle>
 <QuickStartGrid>
 {QUICK_START_FAQ.map((item) => (
 <QuickStartCard key={item.title}>
 <QuickStartCardTitle>{item.title}</QuickStartCardTitle>
 <QuickStartCardDescription>
  {item.description}
 </QuickStartCardDescription>
 </QuickStartCard>
 ))}
 </QuickStartGrid>
 </FAQCard>
 </FAQSectionWrapper>
 */}
      <ScaleUpgradeModal
        open={scaleUpgradeOpen}
        onClose={() => setScaleUpgradeOpen(false)}
      />
    </div>
  );
};

// Styled Components
/** Outer shell: one card so header + body read as a single import section */
const ImportDashboardSection = styled.div`
  margin: 2.5rem 0 2rem 0;
  border-radius: 20px;
  overflow: hidden;
  background: var(--color-bg-secondary, #fff);
  border: 1px solid var(--color-border-light, #e5e7eb);
  box-shadow: 0 8px 28px rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    margin: 1.5rem 0 1.5rem 0;
    border-radius: 16px;
  }
`;

/** Wraps the import body for height/opacity expand/collapse (framer-motion). */
const ImportPortfolioPanelMotion = styled(motion.div)`
  overflow: hidden;
`;

const ImportPortfolioPanel = styled.div`
  background: var(--color-bg-secondary, #fff);
  padding: 1.5rem 1.75rem 1.75rem;
  margin: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: none;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem 1.5rem;
  }
`;

const ImportPortfolioHeading = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.75rem 0;
  line-height: 1.35;
`;

const ImportPortfolioFinePrint = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  line-height: 1.45;
  text-align: center;
`;

const ImportDashboardSectionHeader = styled.div`
  font-family: var(--font-primary);
  margin: 0;
  padding: 1rem 1.25rem 1rem 1.5rem;
  background: var(--color-primary);
  border-radius: 0;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-shadow: none;
  position: relative;
  overflow: hidden;

  /* Ensure text/icon on the minimize/maximize header is consistently white */
  button.ant-btn,
  button.ant-btn:where(.ant-btn-text) {
    color: #fff !important;
  }
  button.ant-btn svg,
  button.ant-btn .anticon {
    color: #fff !important;
    fill: #fff !important;
  }

  @media ${device.mobile} {
    padding: 1rem 1.25rem;
    flex-wrap: wrap;
  }
`;

const ImportDashboardSectionHeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
  color: #ffffff;
  font-size: 1.35rem;
  font-weight: 700;
  position: relative;
  z-index: 1;
  ${(props) =>
    props.$clickable &&
    `
 cursor: pointer;
 text-align: left;
 &:focus-visible {
 outline: 2px solid rgba(255, 255, 255, 0.95);
 outline-offset: 2px;
 border-radius: 6px;
 }
 `}
`;

/** Collapsed: title + hint stacked on mobile; hint to the right of title on tablet/desktop (≥768px). */
const ImportDashboardSectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
  text-align: left;

  @media (min-width: 768px) {
    ${(p) =>
      p.$collapsed &&
      `
 flex-direction: row;
 align-items: baseline;
 flex-wrap: wrap;
 gap: 0.75rem 1rem;
 `}
  }
`;

const ImportDashboardSectionTitleText = styled.div`
  flex-shrink: 0;
  color: #ffffff;
  /* @layer base sets div font-weight: 400; match banner title weight */
  font-weight: 700;
`;

const ImportDashboardSectionHint = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  opacity: 0.9;
  line-height: 1.35;
`;

const ImportPortfolioBody = styled.p`
  margin: 0;
  color: var(--color-text-light);
  font-size: 0.9375rem;
  line-height: 1.55;
`;

const DashboardContainer = styled.div`
  padding: 2rem;
  background: var(--color-bg-primary);
  min-height: 100vh;
  position: relative;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  .dashboard-container {
    border: 0px;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  /* Apply the new container class for better background */
  &.dashboard-container {
    background: var(--color-bg-primary) !important;
  }
`;

const WelcomeSection = styled.div`
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const WelcomeTitle = styled.h1`
  font-family: var(--font-primary);
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
  white-space: nowrap;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2.25rem;
    white-space: normal;
  }

  @media ${device.tablet} {
    font-size: 2.75rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 500;
  line-height: 1.6;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const DashboardContent = styled.div`
  min-height: 400px;

  @media ${device.tablet} {
    padding: 0 1.5rem;
  }
`;

const DashboardTabsWrapper = styled.div`
  width: 100%;
`;

const OsWorkspaceTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
`;

const OsWorkspaceTab = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(44, 95, 45, 0.35);
  background: rgba(255, 255, 255, 0.92);
  color: var(--color-text-primary);
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: #fff;
  }
`;

const LeftAlignAlertWrapper = styled.div`
  .ant-alert-content,
  .ant-alert-message,
  .ant-alert-description {
    text-align: left;
  }
`;

const LeoActivityStrip = styled.div`
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  border-radius: 12px;
  padding: 10px 16px;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.2);
`;
const LeoActivityInner = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  color: white;
  font-size: 0.95rem;
`;

const SilenceMetricsCard = styled.div`
  width: 100%;
  border-radius: 18px;
  padding: 1.25rem 1.35rem;
  cursor: pointer;
  text-align: left;
  border: 2px solid rgba(44, 95, 45, 0.2);
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.12) 0%,
    rgba(44, 95, 45, 0.06) 50%,
    #ffffff 100%
  );
  box-shadow: 0 8px 28px rgba(44, 95, 45, 0.12);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  outline: none;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 36px rgba(44, 95, 45, 0.18);
  }
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.35);
  }
`;

const SilenceMetricsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.75rem;
  width: 100%;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SilenceMetricsLeft = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const SilenceMetricsRight = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  text-align: right;
  gap: 0.2rem;

  @media ${device.mobile} {
    align-items: flex-start;
    text-align: left;
    border-top: 1px solid rgba(44, 95, 45, 0.12);
    margin-top: 0.25rem;
    padding-top: 0.75rem;
  }
`;

const SilenceMetricsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0;
`;

const SilenceMetricsIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--gradient-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
`;

const SilenceMetricsLabel = styled.div`
  font-family: var(--font-primary);
  font-weight: 700;
  font-size: 1rem;
  color: var(--color-text);
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const SilenceMetricsSub = styled.div`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin-top: 2px;
`;

const SilenceMetricsValue = styled.div`
  font-family: var(--font-primary);
  font-size: 2.25rem;
  font-weight: 800;
  color: var(--color-primary-dark);
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.03em;
  white-space: nowrap;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const SilenceMetricsFoot = styled.div`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  line-height: 1.4;
  max-width: 280px;

  @media ${device.mobile} {
    max-width: none;
  }
`;

// New styled components for the updated layout
const SectionTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.35rem;
  font-weight: 700;
  color: #ffffff !important;
  text-align: center;
  margin: 2.5rem 0 1.5rem 0;
  padding: 1rem 1.5rem;
  background: var(--color-primary);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.2);
  position: relative;
  overflow: hidden;
  letter-spacing: -0.02em;
  line-height: 1.25;

  .anticon {
    color: #ffffff !important;
    font-size: 1.5rem;
  }

  @media ${device.mobile} {
    font-size: 1.2rem;
    margin: 1.5rem 0 0.75rem 0;
    flex-direction: row;
    gap: 0.5rem;
    padding: 0.9rem 1.25rem;
  }

  @media ${device.tablet} {
    font-size: 1.3rem;
    margin: 1.75rem 0 0.875rem 0;
    padding: 1rem 1.5rem;
  }
`;

const ReportDetailsRow = styled(Row)`
  display: flex;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-right: 0 !important;
    row-gap: 12px !important;

    > .ant-col {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }

  > .ant-col {
    display: flex;
  }

  > .ant-col > * {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-card {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Grow the main card content only — not the mobile tooltip hint */
  .ant-card-body > div:first-child {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const SectionSeparator = styled.div`
  margin: 4rem 0 0;
  height: 1px;
  background: rgba(226, 232, 240, 0.8);
  width: 100%;

  @media (max-width: 768px) {
    margin: 3rem 0 0;
  }
`;

const FAQSectionWrapper = styled.div`
  margin-top: 2rem;
  padding-top: 2.5rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding-top: 2rem;
  }
`;

const FAQCard = styled(Card)`
  margin: 0 0 2rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
  border: 2px solid var(--color-border-light);
  width: 100%;

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  .ant-card-head-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.25rem;
    padding: 1.5rem 2rem 1rem 2rem;
    text-align: center;
    flex: 1;
  }

  .ant-card-body {
    padding: 2rem 2.75rem;

    @media (max-width: 768px) {
      padding: 1.5rem;
    }
  }
`;

const FAQHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: white !important;

  span {
    color: white !important;
  }
`;

const FAQHeaderIcon = styled.span`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;

  .anticon {
    color: white !important;
  }
`;

const FAQSubtitle = styled.p`
  font-size: 1.05rem;
  color: #64748b;
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-weight: 500;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const QuickStartGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 100%;
  grid-template-columns: 1fr;
`;

const QuickStartCard = styled.div`
  position: relative;
  border-radius: 16px;
  padding: 1.75rem;
  background: #ffffff;
  border: 2px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, var(--color-primary) 0%, #5dd77a 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(44, 95, 45, 0.2);
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.12);

    &::before {
      opacity: 1;
    }
  }
`;

const QuickStartCardTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  font-family: var(--font-primary);
  line-height: 1.3;
`;

const QuickStartCardDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.65;
  font-weight: 400;
`;

const SectionIcon = styled.span`
  font-size: 2rem;
  opacity: 0.9;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  margin-top: -0.5rem;

  ${(props) => {
    switch (props.$color) {
      case "primary":
        return "color: var(--color-accent);";
      case "warning":
        return "color: #fbbf24;";
      case "info":
        return "color: #60a5fa;";
      case "white":
        return "filter: brightness(0) invert(1);";
      default:
        return "color: var(--color-accent);";
    }
  }}
`;

const OverviewCard = styled(Card)`
  border-radius: 16px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  /* Add subtle pulse animation to indicate clickability */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 0;
  }

  /* Add arrow indicator */
  &::after {
    content: "→";
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.2rem;
    font-weight: bold;
    color: var(--color-primary);
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
    z-index: 2;
  }

  /* Variant-based styling - using subtle, professional colors */
  ${(props) => {
    switch (props.$variant) {
      case "rent":
        return `
 border-left: 4px solid var(--color-primary);
 background: ${
   props.$priority === "normal"
     ? "linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(44, 95, 45, 0.02) 100%)"
     : "rgba(44, 95, 45, 0.02)"
 };
 ${
   props.$priority === "normal"
     ? `border-left: 4px solid var(--color-success);`
     : ""
 }
 `;
      case "occupancy":
        return `
 border-left: 4px solid var(--color-success);
 background: rgba(16, 185, 129, 0.02);
 `;
      case "leases":
        return `
 border-left: 4px solid var(--color-warning);
 background: rgba(245, 158, 11, 0.02);
 `;
      case "repairs":
        return `
 border-left: 4px solid var(--color-primary);
 background: rgba(44, 95, 45, 0.02);
 `;
      case "reports":
        return `
 border-left: 4px solid var(--color-info);
 background: rgba(96, 165, 250, 0.02);
 `;
      default:
        return "";
    }
  }}

  /* Priority-based styling - using subtle, professional colors */
 ${(props) => {
    switch (props.$priority) {
      case "high":
        return `
 border-color: var(--color-warning);
 box-shadow: 0 4px 20px rgba(245, 158, 11, 0.1);
 
 &:hover {
 box-shadow: 0 8px 30px rgba(245, 158, 11, 0.15);
 }
 `;
      case "medium":
        return `
 border-color: var(--color-primary);
 box-shadow: 0 4px 20px rgba(44, 95, 45, 0.1);
 
 &:hover {
 box-shadow: 0 8px 30px rgba(44, 95, 45, 0.15);
 }
 `;
      default:
        return "";
    }
  }}
 
 &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: var(--color-primary);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
      transform: translateX(0);
    }

    .card-icon {
      transform: scale(1.15);
      opacity: 1;
    }

    .card-number {
      transform: scale(1.05);
    }
  }

  &:active {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12);
    transition: all 0.1s ease;
  }

  /* Focus state for accessibility */
  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  .ant-card-body {
    padding: 2.34rem 1.275rem !important;
    text-align: center;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 0;
    gap: 0;

    /* Ensure all text in cards is readable */
    * {
      color: inherit;
    }

    .ant-statistic-title {
      color: #374151 !important;
      font-weight: 500;
    }

    .ant-statistic-content {
      color: #1e293b !important;
    }
  }

  /* Mobile responsiveness */
  @media ${device.mobile} {
    margin-bottom: 0;

    .ant-card-body {
      padding: 1.25rem 1rem !important;
    }

    &::after {
      display: none;
    }

    &:hover {
      transform: none;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    &:active {
      transform: none;
    }
  }

  @media ${device.tablet} {
    margin-bottom: 1rem;

    .ant-card-body {
      padding: 1.5rem 1.15rem !important;
    }

    &:hover {
      transform: translateY(-2px) scale(1.01);
    }

    &::after {
      font-size: 1rem;
      top: 0.75rem;
      right: 0.75rem;
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 0;
  position: relative;
  box-sizing: border-box;
  padding: 0.25rem 0;

  .anticon svg {
    width: 24px;
    height: 24px;
  }
`;

const CardIcon = styled.div.attrs({
  className: "card-icon",
})`
  font-size: 1.75rem;
  line-height: 1;
  margin-bottom: 0.75rem;
  opacity: 0.8;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    switch (props.$variant) {
      case "rent":
        return "color: #059669;";
      case "occupancy":
        return "color: #059669;";
      case "leases":
        return "color: #d97706;";
      case "repairs":
        return "color: #059669;";
      case "reports":
        return "color: #2563eb;";
      default:
        return "color: #1e293b;";
    }
  }}
`;

const CardTitle = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.875rem; /* 14px */
  font-weight: 600;
  color: #1e293b !important;
  text-align: center;
  line-height: 1.4;
  margin-bottom: 0.5rem;
`;

const CardNumber = styled.div.attrs({
  className: "card-number",
})`
  font-family: var(--font-primary) !important;
  font-size: 24px !important; /* Use px instead of rem for more specificity */
  font-weight: 700 !important;
  color: #1e293b !important;
  text-align: center;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  line-height: 1.2 !important;

  /* Force override for all child elements */
  * {
    font-size: 24px !important;
    font-weight: 700 !important;
    line-height: 1.2 !important;
  }

  /* Override any Ant Design styles */
  &.ant-statistic-content,
  &.ant-statistic-content-value,
  &.ant-statistic-content-suffix,
  &.ant-statistic-content-prefix {
    font-size: 24px !important;
    font-weight: 700 !important;
  }

  @media ${device.mobile} {
    font-size: 20px !important;

    * {
      font-size: 20px !important;
    }

    &.ant-statistic-content,
    &.ant-statistic-content-value,
    &.ant-statistic-content-suffix,
    &.ant-statistic-content-prefix {
      font-size: 20px !important;
    }
  }
`;

const CardSubtitle = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.875rem; /* 14px - same as title */
  color: #374151 !important;
  text-align: center;
  margin-bottom: 0.75rem;
  line-height: 1.3;
`;

const MobileCardHint = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex: 0 0 auto;
    font-size: 0.7rem;
    color: var(--color-text-light, #6b7280);
    line-height: 1.35;
    text-align: center;
    padding: 0.65rem 0.35rem 0;
    margin-top: 0.65rem;
    border-top: 1px solid var(--color-border-light, #e5e7eb);
  }
`;

const CardStatus = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.$status) {
      case "success":
        return `
 background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
 color: var(--color-success);
 box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
 font-weight: 700;
 `;
      case "warning":
        return `
 background: var(--color-warning-glow);
 color: var(--color-warning);
 `;
      case "error":
        return `
 background: var(--color-error-glow);
 color: var(--color-error);
 `;
      case "info":
      default:
        return `
 background: var(--color-info-glow);
 color: var(--color-info);
 `;
    }
  }}
`;

const ReportIcon = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 1.25rem;
`;

const RentManagementButton = styled(Button)`
  background: var(--color-primary);
  color: white;
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  font-family: var(--font-secondary);
  font-weight: 500;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-top: 0.5rem;

  &:hover {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActionButton = styled(Button)`
  background: var(--color-primary);
  color: white;
  border: 2px solid var(--color-primary);
  border-radius: 10px;
  font-family: var(--font-secondary);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.75rem 1.5rem;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);

  &:hover {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.25);
  }

  &:active {
    transform: translateY(0);
    transition: all 0.1s ease;
  }

  .anticon {
    font-size: 16px;
  }

  @media ${device.mobile} {
    padding: 0.625rem 1.25rem;
    font-size: 0.9rem;
    height: 36px;
  }
`;

// Global CSS to disable tooltips on mobile for dashboard tiles
const GlobalDashboardStyle = styled.div`
  @media (max-width: 768px) {
    .dashboard-tooltip {
      pointer-events: none;

      .ant-tooltip {
        display: none !important;
      }

      /* Ensure the tooltip wrapper doesn't interfere with touch events */
      &:hover .ant-tooltip,
      &:focus .ant-tooltip,
      &:active .ant-tooltip {
        display: none !important;
      }
    }

    /* Disable tooltips for dashboard tiles on mobile */
    .dashboard-container .ant-card .ant-tooltip,
    .dashboard-container .ant-card .dashboard-tooltip .ant-tooltip {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  }
`;

// Lease Expirations Modal Styled Components
const LeaseExpirationsContent = styled.div`
  .ant-list-item {
    border: none;
    padding: 0;
    margin-bottom: 1rem;
  }
`;

const TenantExpirationCard = styled.div`
  width: 100%;
  padding: 1.25rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
  }
`;

const TenantName = styled.div`
  font-family: var(--font-primary);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.25rem;
`;

const TenantUnit = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
`;

const ExpirationDate = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const DaysRemaining = styled.div`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) =>
    props.$urgent ? "var(--color-error)" : "var(--color-warning)"};
  margin-bottom: 0.25rem;
`;

const MonthlyRent = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-primary);
`;

/* DSCR modal: matches AddExpenseModal / InvoiceAdjustmentModal branding */
const StyledDscrModal = styled(Modal)`
  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }
  .ant-modal-header,
  .ant-modal-header * {
    color: #fff !important;
  }
  .ant-modal-title {
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }
  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 16px;
    right: 20px;
    &:hover {
      color: rgba(255, 255, 255, 0.85) !important;
    }
  }
  .ant-modal-body {
    padding: 24px;
    background: var(--color-bg-primary, #fff);
    overflow-x: hidden;
    overflow-y: auto;
    font-family: var(--font-secondary);
    font-size: 0.9375rem;
    line-height: 1.6;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
  }
  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--color-border, #e5e7eb);
    background: var(--color-bg-secondary, #f9fafb);
    border-radius: 0 0 12px 12px;
  }
  .ant-modal-footer .ant-btn-primary {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border: none;
    border-radius: 8px;
    font-weight: 600;
  }
  .ant-modal-footer .ant-btn-default {
    border-radius: 8px;
    border: 2px solid var(--color-primary);
    color: var(--color-primary);
    font-weight: 500;
  }
  .ant-modal-footer .ant-btn-default:hover {
    color: white;
    border-color: var(--color-primary);
    background: var(--color-primary);
  }
  @media (max-width: 768px) {
    .ant-modal-wrap {
      padding: 12px 0;
      align-items: flex-start;
    }
    .ant-modal {
      max-width: 95vw !important;
      width: 95vw !important;
      margin: 0 auto;
      padding-bottom: 12px;
    }
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 24px);
    }
    .ant-modal-header {
      flex-shrink: 0;
    }
    .ant-modal-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 16px;
      -webkit-overflow-scrolling: touch;
    }
    .ant-modal-footer {
      flex-shrink: 0;
      padding: 12px 16px;
      flex-wrap: wrap;
      gap: 8px;
    }
    .ant-modal-footer .ant-btn {
      min-width: 0;
      flex: 1 1 auto;
    }
  }
  @media (max-width: 480px) {
    .ant-modal-wrap {
      padding: 8px 0 12px;
    }
    .ant-modal {
      max-width: 98vw !important;
      width: 98vw !important;
      margin: 0 auto;
    }
    .ant-modal-content {
      max-height: calc(100vh - 20px);
      border-radius: 12px;
    }
    .ant-modal-body {
      padding: 12px 16px;
    }
    .ant-modal-footer {
      padding: 12px 16px;
    }
  }
`;

const DscrModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
`;

const DscrModalSection = styled.section`
  margin-bottom: 1rem;
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const DscrModalSectionCard = styled.div`
  font-size: 0.9375rem;
  line-height: 1.6;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  border: 1px solid
    ${(props) => {
      if (props.$variant === "green") return "rgba(44, 95, 45, 0.2)";
      if (props.$variant === "blue") return "rgba(59, 130, 246, 0.25)";
      return "var(--color-border)";
    }};
  background: ${(props) => {
    if (props.$variant === "green") return "rgba(44, 95, 45, 0.1)";
    if (props.$variant === "gray") return "var(--color-bg-secondary, #f3f4f6)";
    if (props.$variant === "blue") return "rgba(59, 130, 246, 0.08)";
    return "var(--color-bg-primary, #fff)";
  }};
  &:last-of-type {
    margin-bottom: 0;
  }
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
  }
`;

const DscrModalCollapse = styled(Collapse)`
  font-size: 0.9375rem;
  line-height: 1.6;
  border: none !important;
  background: transparent !important;
  .ant-collapse-item {
    border-bottom: 1px solid var(--color-border);
    &:last-child {
      border-bottom: none;
    }
  }
  .ant-collapse-header {
    font-weight: 600;
    color: var(--color-text);
    font-size: 0.9375rem;
    padding: 0.5rem 0 !important;
    padding-right: 1.5rem !important;
    align-items: center;
  }
  .ant-collapse-content-box {
    font-size: 0.9375rem;
    color: var(--color-text);
    line-height: 1.5;
    padding: 0 0 0.5rem 0 !important;
  }
  .ant-collapse-arrow {
    color: var(--color-primary);
  }
`;

const DscrModalSectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  .anticon {
    font-size: 1.125rem;
    color: var(--color-primary);
  }
`;

const DscrModalSectionTitle = styled.p`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  line-height: 1.4;
`;

const DscrModalBody = styled.p`
  font-size: 0.9375rem;
  color: var(--color-text);
  line-height: 1.6;
  margin-bottom: 0.75rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const DscrModalList = styled.ul`
  margin: 0 0 0.5rem 0;
  padding-left: 1.25rem;
  font-size: 0.9375rem;
  color: var(--color-text);
  line-height: 1.6;
  li {
    margin-bottom: 0.5rem;
  }
  li:last-child {
    margin-bottom: 0;
  }
`;

const DscrModalInputLabel = styled.p`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
`;

const DscrModalHint = styled.p`
  font-size: 0.75rem;
  color: var(--color-text-light);
  margin-top: 0.75rem;
  margin-bottom: 0;
  line-height: 1.4;
`;

const DscrModalActionButton = styled(Button)`
  border-radius: 8px;
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  font-weight: 500;
  &:hover {
    color: white !important;
    border-color: var(--color-primary) !important;
    background: var(--color-primary) !important;
  }
`;

export default Dashboard;
